"use client";

import { useState } from "react";
import { runChat, AiToolError, type ChatMessage } from "@/lib/aiTools";
import { ErrorBox, FreeTrialNote } from "@/components/aiTools/ToolUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

export default function ChatTool() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    setError(null);
    setCapped(false);
    try {
      const reply = await runChat(next);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e) {
      if (e instanceof AiToolError && e.status === 429) {
        setCapped(true);
        setError(e.message);
      } else {
        setError(e instanceof AiToolError ? e.message : "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="max-h-96 min-h-[120px] space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-900/40">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">Ask anything to get started.</p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <p
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-violet-600 text-white"
                    : "bg-white text-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
                }`}
              >
                {m.content}
              </p>
            </div>
          ))
        )}
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500">
            <Spinner className="h-4 w-4" />
            Thinking…
          </p>
        ) : null}
      </div>
      {error ? <ErrorBox message={error} /> : null}
      {capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      <div className="mt-3 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
        <button
          type="button"
          onClick={send}
          disabled={loading || !input.trim()}
          className="rounded-full bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
        >
          Send
        </button>
      </div>
      <FreeTrialNote />
    </div>
  );
}
